import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DnsIcon from '@mui/icons-material/Dns';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentIcon from '@mui/icons-material/Payment';
import PublishIcon from '@mui/icons-material/Publish';
import { getAuth } from 'firebase/auth';
import { ArticleDraftService, WebPlacementService, type ArticleDraft } from '@modules/firebase-core';
import { ArticleFolderService } from '@modules/firebase-core/services/ArticleFolderService';
import type { ArticleFolder } from '@modules/firebase-core/converters/articleFolderConverter';
import {
  SDataTable,
  useSelectionMode,
  SelectionActionsBar,
  HeaderSearchField,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { CreateFolderDialog } from '@modules/onboardings/components/CreateFolderDialog';
import { MoveToFolderDialog } from '@modules/onboardings/components/MoveToFolderDialog';
import { ArticleDetailsDialog } from '@modules/support-flow/components/ArticleDetailsDialog';
import { ArticlePublishApiClient } from '@modules/support-flow/services/ArticlePublishApiClient';
import { ArticleWebPublishService } from '@modules/support-flow/services/ArticleWebPublishService';
import { ARTICLE_SITE_DOMAIN_BUTTON_TEST_ID } from '@modules/support-flow/constants/articleSiteTestIds';
import { articleStatusChipProps } from '@modules/support-flow/utils/articleStatusChip';
import {
  readArticleFolderMemory,
  writeArticleFolderMemory,
} from '@modules/support-flow/utils/articleFolderMemory';
import { WebPlacementDialog, DeployWebPlacementDialog } from '@modules/webplacements';
import type { CreateWebPlacementInput, UpdateWebPlacementInput, WebPlacement, WebPlacementDeployResult } from '@modules/webplacements/types/webplacements.types';
import { useNotification } from '@frontend/contexts/NotificationContext';

/**
 * Union row model — folders render as rows of the same SDataTable
 * (folder-table recipe, playbook 13-DataTables.mdx). Folder row ids are
 * namespaced so selection keys never collide with article ids.
 */
type ArticleTableRow =
  | { type: 'folder'; id: string; folder: ArticleFolder }
  | { type: 'article'; id: string; draft: ArticleDraft };

type MoveTarget = { kind: 'single'; draft: ArticleDraft } | { kind: 'bulk' };
type DeleteTarget =
  | { kind: 'article'; draft: ArticleDraft }
  | { kind: 'folder'; folder: ArticleFolder }
  | { kind: 'bulk' };

export function ArticlesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const notifications = useNotification();
  const articleService = useMemo(() => new ArticleDraftService(), []);
  const folderService = useMemo(() => new ArticleFolderService(), []);
  const publishClient = useMemo(() => new ArticlePublishApiClient(), []);
  const webPublishService = useMemo(() => new ArticleWebPublishService(), []);
  const placementService = useMemo(() => new WebPlacementService(), []);

  const [drafts, setDrafts] = useState<ArticleDraft[]>([]);
  const [folders, setFolders] = useState<ArticleFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [webPublishingId, setWebPublishingId] = useState<string | null>(null);
  const [cloningId, setCloningId] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Folder navigation (localStorage-remembered per project)
  // ------------------------------------------------------------------
  const [currentFolderId, setCurrentFolderIdState] = useState<string | null>(
    () => (projectId ? readArticleFolderMemory(projectId).folderId : null),
  );

  const setCurrentFolderId = useCallback((folderId: string | null) => {
    setCurrentFolderIdState(folderId);
    if (projectId) writeArticleFolderMemory(projectId, folderId);
  }, [projectId]);

  // Gmail entry (checkboxReveal): mode is ALWAYS on — the header checkbox is
  // the selection entry, the batch bar shows whenever something is selected.
  const selection = useSelectionMode({ defaultMode: true });

  // Selection is scoped to the open folder — clear on navigation (NOT exit():
  // exiting would turn the always-on mode off for good).
  useEffect(() => {
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [draftsResult, foldersResult] = await Promise.all([
        articleService.listDrafts(projectId),
        folderService.list(projectId),
      ]);
      setDrafts(draftsResult);
      setFolders(foldersResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [articleService, folderService, projectId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Remembered folder may have been deleted elsewhere — fall back to root.
  useEffect(() => {
    if (loading || !currentFolderId) return;
    if (!folders.some((folder) => folder.id === currentFolderId)) {
      setCurrentFolderId(null);
    }
  }, [loading, folders, currentFolderId, setCurrentFolderId]);

  const currentFolder = useMemo(
    () => folders.find((folder) => folder.id === currentFolderId) ?? null,
    [folders, currentFolderId],
  );

  const articleCountByFolder = useMemo(() => {
    const counts = new Map<string, number>();
    for (const draft of drafts) {
      if (draft.folderId) {
        counts.set(draft.folderId, (counts.get(draft.folderId) ?? 0) + 1);
      }
    }
    return counts;
  }, [drafts]);

  // Folders-first by construction (recipe: never via sorting). Root shows
  // folders + orphan-safe unfoldered articles (legacy docs lack folderId;
  // drafts pointing at deleted folders stay visible instead of vanishing).
  const rows = useMemo<ArticleTableRow[]>(() => {
    const folderIds = new Set(folders.map((folder) => folder.id));
    const folderRows: ArticleTableRow[] =
      currentFolderId === null
        ? folders.map((folder) => ({ type: 'folder' as const, id: `folder:${folder.id}`, folder }))
        : [];
    const articleRows: ArticleTableRow[] = drafts
      .filter((draft) =>
        currentFolderId === null
          ? !draft.folderId || !folderIds.has(draft.folderId)
          : draft.folderId === currentFolderId,
      )
      .map((draft) => ({ type: 'article' as const, id: draft.id, draft }));
    return [...folderRows, ...articleRows];
  }, [drafts, folders, currentFolderId]);

  // ------------------------------------------------------------------
  // Create / clone / publish handlers
  // ------------------------------------------------------------------
  const handleCreate = useCallback(async () => {
    if (!projectId) return;
    setCreating(true);
    setError(null);
    try {
      const draft = await articleService.createDraft(projectId, {
        title: 'Untitled article',
        // New article lands in the folder the user is looking at.
        folderId: currentFolderId,
      });
      navigate(`/project/${projectId}/article/${draft.id}/edit`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create article';
      setError(message);
      notifications?.showNotification?.(message, 'error');
    } finally {
      setCreating(false);
    }
  }, [articleService, currentFolderId, navigate, notifications, projectId]);

  const handlePublish = useCallback(async (draft: ArticleDraft) => {
    if (!projectId) return;
    setPublishingId(draft.id);
    setError(null);
    try {
      const publishResult = await publishClient.publish({
        projectId,
        alias: draft.alias,
        flowDocument: draft.flowDocument,
        locale: draft.defaultLanguage,
        title: draft.title,
      });
      const published = await articleService.markPublished(projectId, draft.id, {
        // Prefer the user's article domain, then the clean /a/ gateway URL;
        // the raw assets URL is the storage location, not the public link.
        url: publishResult.customDomainUrl || publishResult.gatewayUrl || publishResult.url,
        assetUrl: publishResult.url,
        configUrl: publishResult.configUrl,
        gatewayUrl: publishResult.gatewayUrl,
        customDomainUrl: publishResult.customDomainUrl,
        alias: publishResult.alias,
        webShellVersion: publishResult.webShellVersion,
      });
      setDrafts((current) => current.map((item) => item.id === draft.id ? published : item));
      notifications?.showNotification?.('Article published', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish article';
      setError(message);
      notifications?.showNotification?.(message, 'error');
    } finally {
      setPublishingId(null);
    }
  }, [articleService, notifications, projectId, publishClient]);

  // "Publish with payments (web)" (R6b): opens the SAME placement settings
  // dialog as onboarding web publish (payments, redirect, data collection,
  // entry rules, SEO, webhooks) before every publish. The backing placement
  // is tagged sourceKind:'article' — listed on the Placements page with an
  // «Article» badge, excluded from onboarding-funnel pickers. The CDN publish
  // (/a/{alias}) stays independent.
  const [publishDialogDraft, setPublishDialogDraft] = useState<ArticleDraft | null>(null);
  const [publishDialogPlacement, setPublishDialogPlacement] = useState<WebPlacement | null>(null);
  const [deployDraft, setDeployDraft] = useState<ArticleDraft | null>(null);
  const [deployPlacement, setDeployPlacement] = useState<WebPlacement | null>(null);

  const handlePublishWithPayments = useCallback(async (draft: ArticleDraft) => {
    if (!projectId) return;
    setWebPublishingId(draft.id);
    setError(null);
    try {
      const existing = await placementService.getByAlias(projectId, draft.alias);
      if (existing && existing.sourceKind !== 'article') {
        throw new Error(`Alias "${draft.alias}" is already used by a web placement. Change the article alias.`);
      }
      setPublishDialogPlacement(existing ?? null);
      setPublishDialogDraft(draft);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open publish settings';
      setError(message);
      notifications?.showNotification?.(message, 'error');
    } finally {
      setWebPublishingId(null);
    }
  }, [notifications, placementService, projectId]);

  const closePublishDialog = useCallback(() => {
    setPublishDialogDraft(null);
    setPublishDialogPlacement(null);
  }, []);

  const handleArticlePlacementCreate = useCallback(async (data: Omit<CreateWebPlacementInput, 'createdBy'>) => {
    if (!projectId) throw new Error('Project id is required');
    return placementService.create(projectId, {
      ...data,
      sourceKind: 'article',
      createdBy: getAuth().currentUser?.email ?? 'unknown',
    });
  }, [placementService, projectId]);

  const handleArticlePlacementUpdate = useCallback(async (id: string, data: UpdateWebPlacementInput) => {
    if (!projectId) throw new Error('Project id is required');
    return placementService.update(projectId, id, data);
  }, [placementService, projectId]);

  const isAliasUnique = useCallback(async (alias: string, excludeId?: string) => {
    if (!projectId) return false;
    return placementService.isAliasUnique(projectId, alias, excludeId);
  }, [placementService, projectId]);

  // «Save & Publish» in the dialog → hand off to the standard deploy-progress
  // dialog (same UX as onboarding placements): spinner while publishing, then
  // the final URL with copy/open actions.
  const handleArticleDeploy = useCallback(async (placement: WebPlacement) => {
    const draft = publishDialogDraft;
    if (!projectId || !draft) return;
    closePublishDialog();
    setDeployDraft(draft);
    setDeployPlacement(placement);
  }, [closePublishDialog, projectId, publishDialogDraft]);

  const closeDeployDialog = useCallback(() => {
    setDeployDraft(null);
    setDeployPlacement(null);
  }, []);

  // Executed by DeployWebPlacementDialog (auto-triggered on open): publish the
  // draft FlowDocument through /publish/web with the just-saved placement
  // config; the dialog renders progress, the resulting URL, errors, and retry.
  const executeArticleWebDeploy = useCallback(async (): Promise<WebPlacementDeployResult> => {
    const draft = deployDraft;
    const placement = deployPlacement;
    if (!projectId || !draft || !placement) {
      return { success: false, webUrl: '', gcsFiles: [], error: 'Article draft is no longer available' };
    }
    setWebPublishingId(draft.id);
    try {
      const result = await webPublishService.publish({
        projectId,
        articleId: draft.id,
        alias: draft.alias,
        title: draft.title,
        flowDocument: draft.flowDocument,
        createdBy: getAuth().currentUser?.email ?? 'unknown',
      }, placement);
      if (!result.success) {
        return { success: false, webUrl: result.webUrl ?? '', gcsFiles: [], error: result.error || 'Failed to publish article to web' };
      }
      notifications?.showNotification?.('Article published to web (payments enabled)', 'success');
      return { success: true, webUrl: result.webUrl, gcsFiles: [] };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish article to web';
      return { success: false, webUrl: '', gcsFiles: [], error: message };
    } finally {
      setWebPublishingId(null);
    }
  }, [deployDraft, deployPlacement, notifications, projectId, webPublishService]);

  const handleClone = useCallback(async (draft: ArticleDraft) => {
    if (!projectId) return;
    setCloningId(draft.id);
    setError(null);
    try {
      const clone = await articleService.cloneDraft(projectId, draft.id);
      notifications?.showNotification?.('Article cloned', 'success');
      navigate(`/project/${projectId}/article/${clone.id}/edit`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clone article';
      setError(message);
      notifications?.showNotification?.(message, 'error');
    } finally {
      setCloningId(null);
    }
  }, [articleService, navigate, notifications, projectId]);

  const handleOpenPublished = useCallback((draft: ArticleDraft) => {
    if (!draft.publishedUrl) return;
    window.open(draft.publishedUrl, '_blank', 'noopener,noreferrer');
  }, []);

  // ------------------------------------------------------------------
  // Details (title + alias) + publish-confirm dialogs (R9b)
  // ------------------------------------------------------------------
  const [detailsDraft, setDetailsDraft] = useState<ArticleDraft | null>(null);
  const [publishDraft, setPublishDraft] = useState<ArticleDraft | null>(null);

  const isArticleAliasUnique = useCallback(
    (alias: string, excludeArticleId: string) => {
      if (!projectId) return Promise.resolve(false);
      return articleService.isAliasUnique(projectId, alias, excludeArticleId);
    },
    [articleService, projectId],
  );

  // Edit details: persist, patch the row in place, stay on the page.
  const handleDetailsConfirm = useCallback(async (input: { title?: string; alias: string }) => {
    if (!projectId || !detailsDraft) return;
    const updated = await articleService.updateDetails(projectId, detailsDraft.id, input);
    setDrafts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    setDetailsDraft(null);
    notifications?.showNotification?.('Article details updated', 'success');
  }, [articleService, detailsDraft, notifications, projectId]);

  // Publish confirm: persist the (possibly changed) alias first, close the
  // dialog, then run the CDN publish — progress shows as the row's
  // «Publishing…» chip, not a blocked dialog.
  const handlePublishConfirm = useCallback(async (input: { alias: string }) => {
    if (!projectId || !publishDraft) return;
    let draftToPublish = publishDraft;
    if (input.alias !== publishDraft.alias) {
      draftToPublish = await articleService.updateDetails(projectId, publishDraft.id, {
        alias: input.alias,
      });
      setDrafts((current) => current.map((item) => (item.id === draftToPublish.id ? draftToPublish : item)));
    }
    setPublishDraft(null);
    void handlePublish(draftToPublish);
  }, [articleService, handlePublish, projectId, publishDraft]);

  // ------------------------------------------------------------------
  // Folder operations
  // ------------------------------------------------------------------
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [mutating, setMutating] = useState(false);

  const handleCreateFolder = useCallback(async (name: string) => {
    if (!projectId) throw new Error('Project id is required');
    await folderService.create(projectId, { name });
    await loadData();
  }, [folderService, loadData, projectId]);

  const handleMove = useCallback(async (folderId: string | null) => {
    if (!projectId || !moveTarget) return;
    const ids = moveTarget.kind === 'single' ? [moveTarget.draft.id] : selection.selectedIds;
    await Promise.all(ids.map((id) => articleService.moveToFolder(projectId, id, folderId)));
    notifications?.showNotification?.(
      ids.length === 1 ? 'Article moved' : `${ids.length} articles moved`,
      'success',
    );
    await loadData();
    selection.clearSelection();
  }, [articleService, loadData, moveTarget, notifications, projectId, selection]);

  const requestFolderDelete = useCallback((folder: ArticleFolder) => {
    const count = articleCountByFolder.get(folder.id) ?? 0;
    if (count > 0) {
      notifications?.showNotification?.(
        `Folder "${folder.name}" is not empty — move or delete its ${count} article(s) first`,
        'error',
      );
      return;
    }
    setDeleteTarget({ kind: 'folder', folder });
  }, [articleCountByFolder, notifications]);

  const handleConfirmDelete = useCallback(async () => {
    if (!projectId || !deleteTarget) return;
    setMutating(true);
    try {
      if (deleteTarget.kind === 'article') {
        await articleService.deleteDraft(projectId, deleteTarget.draft.id);
        notifications?.showNotification?.('Article deleted', 'success');
      } else if (deleteTarget.kind === 'bulk') {
        const ids = selection.selectedIds;
        await Promise.all(ids.map((id) => articleService.deleteDraft(projectId, id)));
        notifications?.showNotification?.(`${ids.length} articles deleted`, 'success');
      } else {
        await folderService.delete(projectId, deleteTarget.folder.id);
        notifications?.showNotification?.('Folder deleted', 'success');
      }
      setDeleteTarget(null);
      await loadData();
      selection.clearSelection();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      notifications?.showNotification?.(message, 'error');
    } finally {
      setMutating(false);
    }
  }, [articleService, deleteTarget, folderService, loadData, notifications, projectId, selection]);

  // ------------------------------------------------------------------
  // Table definition
  // ------------------------------------------------------------------
  const columns = useMemo<SDataTableColumnDef<ArticleTableRow>[]>(() => [
    {
      id: 'title',
      header: 'Title',
      // Search must see BOTH branches of the union — folder names match too.
      accessorFn: (row) => (row.type === 'folder' ? row.folder.name : row.draft.title),
      filterFn: 'contains',
      Header: ({ column }) => (
        <HeaderSearchField
          label="Title"
          value={(column.getFilterValue() as string) ?? ''}
          onChange={(next) => column.setFilterValue(next === '' ? undefined : next)}
          testId="articles-search-input"
        />
      ),
      Cell: ({ row }) =>
        row.original.type === 'folder' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <FolderIcon color="primary" fontSize="small" sx={{ flex: 'none' }} />
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
              {row.original.folder.name}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.original.draft.title}
          </Typography>
        ),
    },
    {
      id: 'alias',
      header: 'Alias',
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">
          {row.original.type === 'article' ? row.original.draft.alias : '—'}
        </Typography>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      Cell: ({ row }) => {
        if (row.original.type !== 'article') {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const { draft } = row.original;
        if (publishingId === draft.id) {
          return (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<CircularProgress size={12} color="inherit" />}
              label="Publishing…"
              data-testid="article-publishing-chip"
            />
          );
        }
        return (
          <Chip
            size="small"
            {...articleStatusChipProps(draft.status)}
          />
        );
      },
    },
    {
      id: 'updated',
      header: 'Updated',
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(row.original.type === 'article' ? row.original.draft.updatedAt : row.original.folder.updatedAt)}
        </Typography>
      ),
    },
  ], [publishingId]);

  const renderRowMenuItems = useCallback(({ row, closeMenu }: {
    row: { original: ArticleTableRow };
    closeMenu: () => void;
  }) => {
    if (row.original.type === 'folder') {
      const { folder } = row.original;
      return [
        <MenuItem
          key="delete-folder"
          data-testid="article-menu-delete-folder"
          onClick={() => {
            closeMenu();
            requestFolderDelete(folder);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete folder</ListItemText>
        </MenuItem>,
      ];
    }

    const { draft } = row.original;
    return [
      <MenuItem
        key="edit"
        data-testid="article-menu-edit"
        onClick={() => {
          closeMenu();
          navigate(`/project/${projectId}/article/${draft.id}/edit`);
        }}
      >
        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>,
      <MenuItem
        key="edit-details"
        data-testid="article-menu-edit-details"
        onClick={() => {
          closeMenu();
          setDetailsDraft(draft);
        }}
      >
        <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Edit details</ListItemText>
      </MenuItem>,
      <MenuItem
        key="clone"
        data-testid="article-menu-clone"
        disabled={cloningId === draft.id}
        onClick={() => {
          closeMenu();
          void handleClone(draft);
        }}
      >
        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Clone</ListItemText>
      </MenuItem>,
      <MenuItem
        key="publish"
        data-testid="article-menu-publish"
        disabled={publishingId === draft.id}
        onClick={() => {
          closeMenu();
          setPublishDraft(draft);
        }}
      >
        <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Publish</ListItemText>
      </MenuItem>,
      <MenuItem
        key="publish-web"
        data-testid="article-menu-publish-web"
        disabled={webPublishingId === draft.id}
        onClick={() => {
          closeMenu();
          void handlePublishWithPayments(draft);
        }}
      >
        <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Publish with payments</ListItemText>
      </MenuItem>,
      <MenuItem
        key="open"
        data-testid="article-menu-open"
        disabled={!draft.publishedUrl}
        onClick={() => {
          closeMenu();
          handleOpenPublished(draft);
        }}
      >
        <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Open published</ListItemText>
      </MenuItem>,
      <MenuItem
        key="move"
        data-testid="article-menu-move"
        onClick={() => {
          closeMenu();
          setMoveTarget({ kind: 'single', draft });
        }}
      >
        <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Move to folder</ListItemText>
      </MenuItem>,
      <MenuItem
        key="delete"
        data-testid="article-menu-delete"
        onClick={() => {
          closeMenu();
          setDeleteTarget({ kind: 'article', draft });
        }}
        sx={{ color: 'error.main' }}
      >
        <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>,
    ];
  }, [cloningId, handleClone, handleOpenPublished, handlePublishWithPayments, navigate, projectId, publishingId, requestFolderDelete, webPublishingId]);

  if (!projectId) {
    return <Alert severity="error">Project id is required.</Alert>;
  }

  const deleteDialogCopy = deleteTarget?.kind === 'folder'
    ? {
        title: 'Delete folder?',
        message: `Folder "${deleteTarget.folder.name}" will be permanently deleted.`,
      }
    : deleteTarget?.kind === 'bulk'
      ? {
          title: `Delete ${selection.selectedIds.length} articles?`,
          message: 'The selected article drafts will be permanently deleted. Published copies stay live until unpublished.',
        }
      : {
          title: 'Delete article?',
          message: `"${deleteTarget?.draft.title ?? ''}" will be permanently deleted. Published copies stay live until unpublished.`,
        };

  return (
    <Box data-testid="articles-page" sx={{ p: 3 }}>
      {/* Header — Onboardings-list style: h5 title, outlined folder + contained
        * create. No Refresh (data reloads after every mutation), no Select
        * toggle (the header checkbox is the selection entry). */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} spacing={2}>
        <Typography variant="h5" component="h1">
          Articles
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<DnsIcon />}
            onClick={() => navigate(`/project/${projectId}/articles/site`)}
            data-testid={ARTICLE_SITE_DOMAIN_BUTTON_TEST_ID}
          >
            Site &amp; domain
          </Button>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setCreateFolderOpen(true)}
            data-testid="articles-create-folder-button"
          >
            Create Folder
          </Button>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleCreate}
            disabled={creating}
            data-testid="articles-new-button"
          >
            Create Article
          </Button>
        </Stack>
      </Stack>

      {/* Batch actions — fixed-height slot between title and table: the bar
        * appears inside it without shifting the table (no layout jump). */}
      <SelectionActionsBar
        selection={selection}
        reserveSpace
        onDeleteSelected={() => setDeleteTarget({ kind: 'bulk' })}
        busy={mutating}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<DriveFileMoveIcon fontSize="small" />}
          disabled={mutating}
          sx={{ textTransform: 'none' }}
          onClick={() => setMoveTarget({ kind: 'bulk' })}
          data-testid="articles-bulk-move-button"
        >
          Move to folder
        </Button>
      </SelectionActionsBar>

      {/* Breadcrumb — only inside a folder. */}
      {currentFolder && (
        <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1.5, mb: 1.5 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => setCurrentFolderId(null)}
            sx={{ cursor: 'pointer' }}
            data-testid="articles-breadcrumb-root"
          >
            Root
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FolderIcon color="primary" fontSize="small" />
            {currentFolder.name}
          </Typography>
        </Breadcrumbs>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      <Box data-testid="articles-table">
        <SDataTable<ArticleTableRow>
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          enableSorting={false}
          enableTopToolbar={false}
          enableRowActions
          enablePagination
          initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
          // Gmail entry: header checkbox always visible, row checkboxes
          // hover-revealed; folders excluded per-row (their disabled checkbox
          // is hidden by the wrapper); Esc clears; row click keeps navigating.
          checkboxReveal
          enableColumnFilters
          columnFilterDisplayMode="custom"
          enableRowSelection={(row) => row.original?.type === 'article'}
          state={{ rowSelection: selection.rowSelection, isLoading: loading }}
          onRowSelectionChange={selection.setRowSelection}
          muiTableBodyRowProps={({ row }) => {
            // While isLoading MRT renders skeleton rows with an empty
            // `original` — dereferencing draft/folder there crashes React.
            const original = row.original as ArticleTableRow | undefined;
            if (!original?.type) return { hover: true };
            return {
              hover: true,
              'data-testid': original.type === 'folder'
                ? `article-folder-row-${original.folder.id}`
                : `article-row-${original.draft.id}`,
              onClick: () => {
                if (original.type === 'folder') {
                  setCurrentFolderId(original.folder.id);
                } else {
                  navigate(`/project/${projectId}/article/${original.draft.id}/edit`);
                }
              },
              sx: { cursor: 'pointer' },
            };
          }}
          renderRowActionMenuItems={renderRowMenuItems}
          renderEmptyRowsFallback={({ table }) => {
            const query = table
              .getState()
              .columnFilters?.find((f) => f.id === 'title')?.value as string | undefined;
            if (query) {
              return (
                <Box data-testid="articles-empty-state" sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No articles matching &quot;{query}&quot;
                  </Typography>
                </Box>
              );
            }
            return (
              <Box data-testid="articles-empty-state" sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {currentFolder ? 'Folder is empty' : 'No articles yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentFolder
                    ? 'Create an article here or move existing ones into this folder.'
                    : 'Create the first Flexible Layout article draft.'}
                </Typography>
              </Box>
            );
          }}
        />
      </Box>

      {/* Edit details (title + alias) — kebab entry. */}
      <ArticleDetailsDialog
        open={Boolean(detailsDraft)}
        mode="edit"
        draft={detailsDraft}
        onClose={() => setDetailsDraft(null)}
        isAliasUnique={isArticleAliasUnique}
        onConfirm={handleDetailsConfirm}
      />

      {/* Publish confirm — alias is the public URL, confirmed every publish. */}
      <ArticleDetailsDialog
        open={Boolean(publishDraft)}
        mode="publish"
        draft={publishDraft}
        onClose={() => setPublishDraft(null)}
        isAliasUnique={isArticleAliasUnique}
        onConfirm={handlePublishConfirm}
      />

      {/* Create folder — reuses the onboardings dialog (structurally generic). */}
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onCreate={handleCreateFolder}
      />

      {/* Move to folder — single (kebab) and bulk (batch bar) share the dialog. */}
      <MoveToFolderDialog
        open={Boolean(moveTarget)}
        onClose={() => setMoveTarget(null)}
        onMove={handleMove}
        folders={folders}
        currentFolderId={moveTarget?.kind === 'single' ? moveTarget.draft.folderId ?? null : currentFolderId}
        itemName={moveTarget?.kind === 'single' ? moveTarget.draft.title : `${selection.selectedIds.length} articles`}
      />

      {/* Destructive confirm — explicit Cancel, error primary, no header X. */}
      <SDialog
        open={Boolean(deleteTarget)}
        onClose={() => { if (!mutating) setDeleteTarget(null); }}
        title={deleteDialogCopy.title}
        maxWidth="xs"
        fullWidth
        showCloseButton={false}
        data-testid="article-delete-dialog"
        primaryAction={{
          label: 'Delete',
          color: 'error',
          onClick: handleConfirmDelete,
          loading: mutating,
          'data-testid': 'article-delete-confirm',
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setDeleteTarget(null),
          disabled: mutating,
        }}
      >
        <Typography variant="body2">{deleteDialogCopy.message}</Typography>
      </SDialog>

      {/* Placement settings before EVERY web publish — same form as
        * onboarding placements (R6b, full settings parity). */}
      {publishDialogDraft && (
        <WebPlacementDialog
          open
          onClose={closePublishDialog}
          onCreate={handleArticlePlacementCreate}
          onUpdate={handleArticlePlacementUpdate}
          onDeployRequested={handleArticleDeploy}
          projectId={projectId}
          userId={getAuth().currentUser?.email ?? 'unknown'}
          mode={publishDialogPlacement ? 'edit' : 'create'}
          webPlacement={publishDialogPlacement ?? undefined}
          isAliasUnique={isAliasUnique}
          lockOnboarding
          initialOnboardingSelection={{
            onboardingId: publishDialogDraft.id,
            onboardingName: publishDialogDraft.title || publishDialogDraft.alias,
            versionId: 'article-draft',
            versionName: 'Article draft',
            versionCreatedAt: publishDialogDraft.updatedAt,
          }}
          articleFlowDocument={publishDialogDraft.flowDocument}
        />
      )}

      {/* Publish progress + final URL — same dialog as onboarding placements. */}
      <DeployWebPlacementDialog
        open={Boolean(deployPlacement)}
        onClose={closeDeployDialog}
        webPlacement={deployPlacement}
        onDeploy={executeArticleWebDeploy}
        projectId={projectId}
      />
    </Box>
  );
}

function formatDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export default ArticlesPage;
